import { z } from 'zod';
import {
  userErrorMessage,
  ebookEnvelope,
  ebookDocumentSchema,
  ebookLimitsSchema,
  ebookReleaseSchema,
  ebookPublicationSchema,
  ebookReadingSchema,
  ebookLearningProgressSchema,
  ebookAttemptSchema,
  ebookAttemptSummarySchema,
  ebookAttemptPositionSchema,
  ebookQuestionSchema,
  ebookWorkspaceSchema,
  ebookPageGeometrySchema,
  ebookColorSchema,
  ebookPracticePreviewResultSchema,
  ebookShelfItemSchema,
  ebookQuestionTotalResultSchema,
  ebookDocumentIndexSchema,
  ebookDirectoryPageSchema,
  ebookQuestionDetailSchema,
  ebookArrangementResultSchema,
  type EbookDirectoryQuery,
  ebookCoverUploadResultSchema,
  ebookDeleteDocumentResultSchema,
  ebookStudyPageSchema,
  ebookStudyReplaySchema,
  ebookRankingOptionsSchema,
  ebookRankingPageSchema,
  type EbookRankingQuery,
  type EbookStudyQuery,
  type EbookWorkspace,
  type EbookQuestion,
  type EbookMove,
} from '@wx-weiqi/contracts';
import type { IdentityStore } from '../stores/identity-store';
import { nextRequestId } from './http';
export async function loadEbookOverview(client: Pick<EbookClient, 'list' | 'limits'>) {
  const [shelf, limits] = await Promise.allSettled([client.list(), client.limits()]);
  const message = (reason: unknown) => userErrorMessage(reason, '教材信息加载失败，请重试。');
  return {
    shelf: shelf.status === 'fulfilled' ? shelf.value : null,
    limits: limits.status === 'fulfilled' ? limits.value : null,
    shelfError: shelf.status === 'rejected' ? message(shelf.reason) : '',
    limitsError: limits.status === 'rejected' ? message(limits.reason) : '',
  };
}
// Refresh reads independently after a successful publish. A failed read must
// never be presented as a failed write or trigger another PUBLISH command.
export async function loadPublishedEbook(
  client: Pick<EbookClient, 'document' | 'releases'>,
  id: string,
) {
  const [document, releases] = await Promise.allSettled([client.document(id), client.releases(id)]);
  return {
    document: document.status === 'fulfilled' ? document.value : null,
    releases: releases.status === 'fulfilled' ? releases.value : null,
    incomplete: document.status === 'rejected' || releases.status === 'rejected',
  };
}
export function ebookFileProblem(file: Pick<File, 'size'>, maxBytes: number) {
  if (file.size < 8) return '请选择有效的 PDF 文件。';
  if (file.size > maxBytes)
    return `所选 PDF 为 ${(file.size / 1048576).toFixed(2)} MiB，超过 ${maxBytes / 1048576} MiB 上限。选页只限制识别范围，原文件仍需完整上传。`;
  return '';
}
export class EbookClient {
  rankingOptions() {
    return this.read('/rankings/options', ebookRankingOptionsSchema);
  }
  rankings(query: EbookRankingQuery) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query))
      if (value !== undefined) params.set(key, String(value));
    return this.read(`/rankings?${params}`, ebookRankingPageSchema);
  }
  private clock: { time: number; received: number } | null = null;
  serverNow() {
    return this.clock ? this.clock.time + performance.now() - this.clock.received : Date.now();
  }
  private syncClock(timestamp: string) {
    const received = performance.now();
    // An idempotent replay or an out-of-order response can carry an older timestamp.
    // Keep the display clock monotonic; the backend still enforces the exact deadline.
    const time = Math.max(
      Date.parse(timestamp),
      this.clock ? this.clock.time + received - this.clock.received : -Infinity,
    );
    this.clock = { time, received };
  }
  constructor(
    private readonly identity: Pick<IdentityStore, 'authorized' | 'authorizedDownload'>,
  ) {}
  async read<T extends z.ZodType>(path: string, schema: T) {
    const response = await this.identity.authorized(`/ebooks${path}`, ebookEnvelope(z.unknown()));
    this.syncClock(response.serverTimestamp);
    return schema.parse(response.data) as z.output<T>;
  }
  async command<T extends z.ZodType>(path: string, body: unknown, schema: T) {
    const response = await this.identity.authorized(`/ebooks${path}`, ebookEnvelope(z.unknown()), {
      method: 'POST',
      requestId: nextRequestId('ebook'),
      body,
    });
    this.syncClock(response.serverTimestamp);
    return schema.parse(response.data) as z.output<T>;
  }
  async largeCommand<T extends z.ZodType>(path: string, body: unknown, schema: T) {
    const formData = new FormData();
    formData.append('command', JSON.stringify(body));
    const response = await this.identity.authorized(`/ebooks${path}`, ebookEnvelope(z.unknown()), {
      method: 'POST',
      requestId: nextRequestId('ebook-edit'),
      formData,
    });
    return schema.parse(response.data) as z.output<T>;
  }
  limits() {
    return this.read('/limits', ebookLimitsSchema);
  }
  list() {
    return this.read('', z.array(ebookShelfItemSchema));
  }
  async setQuestionTotal(
    id: string,
    expectedVersion: number,
    totalQuestionCount: number,
    requestId: string,
  ) {
    const response = await this.identity.authorized(
      `/ebooks/documents/${id}`,
      ebookEnvelope(ebookQuestionTotalResultSchema),
      {
        method: 'POST',
        requestId,
        body: { action: 'SET_QUESTION_TOTAL', expectedVersion, totalQuestionCount },
      },
    );
    this.syncClock(response.serverTimestamp);
    return response.data;
  }
  async uploadCover(id: string, expectedVersion: number, file: File) {
    if (!file.size || file.size > 8 * 1024 * 1024) throw new Error('请选择 8 MB 以内的封面图片');
    const formData = new FormData();
    formData.append('file', file);
    formData.append('expectedVersion', String(expectedVersion));
    const result = await this.identity.authorized(
      `/ebooks/documents/${id}/cover`,
      ebookEnvelope(ebookCoverUploadResultSchema),
      {
        method: 'POST',
        requestId: nextRequestId('ebook-cover'),
        formData,
      },
    );
    this.syncClock(result.serverTimestamp);
    return result.data;
  }
  async coverImage(path: string) {
    return (await this.identity.authorizedDownload(`/ebooks${path}`, 'cover.jpg')).blob;
  }
  document(id: string) {
    return this.read(`/documents/${id}`, ebookDocumentSchema);
  }
  documentIndex(id: string) {
    return this.read(`/documents/${id}/index`, ebookDocumentIndexSchema);
  }
  directory(id: string, query: Partial<EbookDirectoryQuery>) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query))
      if (value !== undefined) params.set(key, String(value));
    return this.read(`/documents/${id}/directory?${params}`, ebookDirectoryPageSchema);
  }
  questionDetail(id: string, questionId: string, expectedVersion?: number) {
    return this.read(
      `/documents/${id}/questions/${encodeURIComponent(questionId)}${expectedVersion === undefined ? '' : `?expectedVersion=${expectedVersion}`}`,
      ebookQuestionDetailSchema,
    );
  }
  async saveQuestion(
    id: string,
    expectedVersion: number,
    question: EbookQuestion,
    requestId: string,
    newChapter?: string,
  ) {
    const formData = new FormData();
    formData.append(
      'command',
      JSON.stringify({
        expectedVersion,
        question,
        ...(newChapter === undefined ? {} : { newChapter }),
      }),
    );
    const response = await this.identity.authorized(
      `/ebooks/documents/${id}/questions/${encodeURIComponent(question.id)}`,
      ebookEnvelope(ebookQuestionDetailSchema),
      { method: 'POST', requestId, formData },
    );
    this.syncClock(response.serverTimestamp);
    return response.data;
  }
  async saveArrangement(
    id: string,
    expectedVersion: number,
    questionId: string,
    included: boolean,
    requestId: string,
  ) {
    const response = await this.identity.authorized(
      `/ebooks/documents/${id}/arrangement`,
      ebookEnvelope(ebookArrangementResultSchema),
      {
        method: 'POST',
        requestId,
        body: { expectedVersion, questionId, included },
      },
    );
    this.syncClock(response.serverTimestamp);
    return response.data;
  }
  async deleteDocument(id: string, expectedVersion: number, requestId: string) {
    const result = await this.identity.authorized(
      `/ebooks/documents/${id}/delete`,
      ebookEnvelope(ebookDeleteDocumentResultSchema),
      { method: 'POST', requestId, body: { expectedVersion, confirmed: true } },
    );
    this.syncClock(result.serverTimestamp);
    return result.data;
  }
  async upload(file: File, password: string, pages: string) {
    const limits = await this.limits();
    const problem = ebookFileProblem(file, limits.maxBytes);
    if (problem) throw new Error(problem);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('filename', file.name.slice(0, 250));
    formData.append('password', password);
    formData.append('pages', pages);
    return (
      await this.identity.authorized(
        '/ebooks/upload',
        ebookEnvelope(z.object({ id: z.string() })),
        { method: 'POST', requestId: nextRequestId('ebook-upload'), formData },
      )
    ).data;
  }
  mutate(id: string, body: unknown) {
    return this.command(
      `/documents/${id}`,
      body,
      z.object({ id: z.string(), releaseId: z.string().nullable() }),
    );
  }
  save(id: string, expectedVersion: number, data: EbookWorkspace) {
    return this.largeCommand(
      `/documents/${id}`,
      { action: 'SAVE', expectedVersion, data },
      z.object({ id: z.string(), releaseId: z.string().nullable() }),
    );
  }

  trial(id: string, question: EbookQuestion, moves: EbookMove[]) {
    return this.largeCommand(
      `/documents/${id}/trial`,
      { question, moves },
      z.object({ stones: z.array(ebookColorSchema.nullable()), currentPlayer: ebookColorSchema }),
    );
  }
  importSgf(id: string, question: EbookQuestion, sgf: string) {
    return this.largeCommand(`/documents/${id}/import-sgf`, { question, sgf }, ebookQuestionSchema);
  }
  practicePreview(id: string, question: EbookQuestion, moves: EbookMove[], move?: EbookMove) {
    return this.largeCommand(
      `/documents/${id}/practice-preview`,
      { question, moves, move },
      ebookPracticePreviewResultSchema,
    );
  }
  classes() {
    return this.read('/classes', z.array(z.object({ classId: z.string(), name: z.string() })));
  }
  releases(id: string) {
    return this.read(`/documents/${id}/releases`, z.array(ebookPublicationSchema));
  }
  release(id: string) {
    return this.read(`/releases/${id}`, ebookReleaseSchema);
  }
  currentRelease(id: string) {
    return this.read(`/releases/${id}/current`, ebookReleaseSchema);
  }
  bookAttempts(id: string) {
    return this.read(`/releases/${id}/book-attempts`, z.array(ebookAttemptSchema));
  }
  studyRecords(id: string, query: EbookStudyQuery) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query))
      if (value !== undefined && value !== '') params.set(key, String(value));
    return this.read(`/documents/${id}/study-records?${params}`, ebookStudyPageSchema);
  }
  studyReplay(id: string, attemptId: string, ply: number) {
    return this.read(
      `/documents/${id}/study-records/${attemptId}?ply=${ply}`,
      ebookStudyReplaySchema,
    );
  }
  attempts(id: string) {
    return this.read(`/releases/${id}/attempts`, z.array(ebookAttemptSchema));
  }
  attemptSummary(id: string) {
    return this.read(`/releases/${id}/attempt-summary`, z.array(ebookAttemptSummarySchema));
  }
  learningProgress(id: string) {
    return this.read(`/releases/${id}/learning-progress`, ebookLearningProgressSchema);
  }
  ownAttempt(id: string, attemptId: string) {
    return this.read(`/releases/${id}/attempts/${attemptId}`, ebookAttemptSchema);
  }
  attemptPosition(id: string, attemptId: string, ply: number) {
    return this.read(
      `/releases/${id}/attempts/${attemptId}/position?ply=${ply}`,
      ebookAttemptPositionSchema,
    );
  }
  attempt(id: string, body: unknown) {
    return this.command(`/releases/${id}/attempts`, body, ebookAttemptSchema);
  }
  reading(id: string) {
    return this.read(`/releases/${id}/reading`, ebookReadingSchema.nullable());
  }
  saveReading(id: string, questionId: string | null, scrollY: number) {
    return this.command(
      `/releases/${id}/reading`,
      { questionId, scrollY },
      z.object({ ok: z.boolean() }),
    );
  }
  revisions(id: string) {
    return this.read(
      `/documents/${id}/revisions`,
      z.array(z.object({ version: z.number(), createdAt: z.string() })),
    );
  }
  revision(id: string, version: number) {
    return this.read(`/documents/${id}/revisions/${version}`, ebookWorkspaceSchema);
  }
  async image(id: string, page: number, source: 'original' | 'enhanced' = 'original') {
    return (
      await this.identity.authorizedDownload(
        `/ebooks/documents/${id}/pages/${page}${source === 'enhanced' ? '?source=enhanced' : ''}`,
        source === 'enhanced' ? 'page-enhanced.jpg' : 'page.png',
      )
    ).blob;
  }
  pageGeometry(id: string, page: number) {
    return this.read(`/documents/${id}/pages/${page}/geometry`, ebookPageGeometrySchema);
  }
  async download(path: string, name: string) {
    const file = await this.identity.authorizedDownload(`/ebooks${path}`, name);
    const url = URL.createObjectURL(file.blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.filename || name;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}
